import { Component, Inject, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ShopService } from '@app/services/shops/shop.service';

export interface ConditionData {
  name: string;
  displayName: string;
  condition: string;
  values: string;
  weightUnit?: string;
  optionalValue?: string;
}

interface ConditionFormControls {
  name: FormControl<string | null>;
  displayName: FormControl<string | null>;
  condition: FormControl<string | null>;
  values: FormControl<string | null>;
  weightUnit: FormControl<string | null>;
  value1: FormControl<string | null>;
  value2: FormControl<string | null>;
  optionalValue: FormControl<string | null>;
  stringParam: FormControl<string | null>;
}

type ConditionGroupForm = FormArray<FormGroup<ConditionFormControls>>;

interface ConditionsDialogFormControls {
  conditions: FormArray<ConditionGroupForm>;
}

export interface BulkEditConditionDialogData {
  shopCurrency?: string;
  existingConditionGroups?: Array<{
    conditions: ConditionData[];
  }>;
  hideAddGroupButton?: boolean;
}

export interface BulkEditConditionDialogResult {
  conditionGroups: Array<{
    conditions: ConditionData[];
  }>;
}

@Component({
  selector: 'bulk-edit-condition-dialog',
  templateUrl: './bulk-edit-condition-dialog.component.html',
  styleUrls: ['./bulk-edit-condition-dialog.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class BulkEditConditionDialogComponent implements OnInit, OnDestroy {
  formGroup: FormGroup<ConditionsDialogFormControls>;
  accordionActiveSection: string[] = ['conditions'];
  loadedCarrier = null;
  hideAddGroupButton = false;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<BulkEditConditionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BulkEditConditionDialogData,
    private formBuilder: FormBuilder,
    private shopService: ShopService,
  ) {}

  ngOnInit() {
    this.hideAddGroupButton = this.data?.hideAddGroupButton ?? false;

    this.formGroup = this.formBuilder.group({
      conditions: this.formBuilder.array<ConditionGroupForm>([]),
    });

    if (this.data?.existingConditionGroups?.length) {
      const conditionsArray = this.formGroup.controls.conditions;

      this.data.existingConditionGroups.forEach((group) => {
        const groupArray = this.formBuilder.array<FormGroup<ConditionFormControls>>([]);

        group.conditions.forEach((condition) => {
          groupArray.push(
            this.formBuilder.group<ConditionFormControls>({
              name: new FormControl(condition.name),
              displayName: new FormControl(condition.displayName),
              condition: new FormControl(condition.condition),
              values: new FormControl(condition.values),
              weightUnit: new FormControl(condition.weightUnit || null),
              value1: new FormControl(condition.values),
              value2: new FormControl(null),
              optionalValue: new FormControl(condition.optionalValue || null),
              stringParam: new FormControl(null),
            }),
          );
        });

        conditionsArray.push(groupArray);
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get conditionsField(): FormArray<ConditionGroupForm> {
    return this.formGroup.controls.conditions;
  }

  hasValidConditions(): boolean {
    if (this.conditionsField.length === 0) return false;

    return this.conditionsField.controls.some((group: ConditionGroupForm) =>
      group.controls.some((condition) => {
        const name = condition.controls.name.value;
        const conditionOp = condition.controls.condition.value;
        const value1 = condition.controls.value1.value;
        return name && conditionOp && this.isFilled(value1);
      }),
    );
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onConfirm() {
    const conditionGroups: BulkEditConditionDialogResult['conditionGroups'] = [];

    this.conditionsField.controls.forEach((group: ConditionGroupForm) => {
      const groupConditions: ConditionData[] = [];

      group.controls.forEach((condition) => {
        const name = condition.controls.name.value;
        const displayName = condition.controls.displayName.value;
        const conditionOp = condition.controls.condition.value;
        const value1 = condition.controls.value1.value;
        const weightUnit = condition.controls.weightUnit.value;
        const optionalValue = condition.controls.optionalValue.value;

        if (name && conditionOp && this.isFilled(value1)) {
          groupConditions.push({
            name,
            displayName: displayName || '',
            condition: conditionOp,
            values: String(value1),
            weightUnit: weightUnit || undefined,
            optionalValue: optionalValue || undefined,
          });
        }
      });

      if (groupConditions.length > 0) {
        conditionGroups.push({ conditions: groupConditions });
      }
    });

    const result: BulkEditConditionDialogResult = { conditionGroups };
    this.dialogRef.close(result);
  }

  private isFilled(v: string | number | null): boolean {
    return v !== null && v !== undefined && v !== '';
  }

  onOpenExpansion(section: string) {}

  onCloseExpansion() {}

  onExpandedNeeded(section: string) {}
}
